import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const categories = ['Meat', 'Veg', 'Dairy', 'Dry', 'Beverage'];
const units = ['Kg', 'Liter', 'Piece'];
const storageTypes = ['Dry', 'Chiller', 'Freezer'];

const generateItemCode = () => `FI-${Date.now().toString().slice(-6)}`;

function FoodItemMaster({ token, selectedPropertyId, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [form, setForm] = useState({
    item_code: generateItemCode(),
    item_name: '',
    category: 'Veg',
    unit: 'Kg',
    reorder_level: '',
    par_stock_level: '',
    preferred_supplier: '',
    cost_price: '',
    storage_type: 'Dry',
    expiry_tracking: false,
    batch_enabled: false,
    is_active: true
  });

  const propertyId = String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const resetForm = () => {
    setEditingId('');
    setForm({
      item_code: generateItemCode(),
      item_name: '',
      category: 'Veg',
      unit: 'Kg',
      reorder_level: '',
      par_stock_level: '',
      preferred_supplier: '',
      cost_price: '',
      storage_type: 'Dry',
      expiry_tracking: false,
      batch_enabled: false,
      is_active: true
    });
  };

  const loadRows = async () => {
    if (!propertyId) return setRows([]);
    try {
      const res = await fetch('/api/v1/food/masters/items', { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load food items');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading food items');
    }
  };

  const loadSuppliers = async () => {
    if (!propertyId) return setSuppliers([]);
    try {
      const res = await fetch('/api/v1/food/masters/suppliers', { headers: headers() });
      const { data } = await parseResponse(res);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch {
      setSuppliers([]);
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      setRows([]);
      return;
    }
    setError('');
    loadRows();
    loadSuppliers();
  }, [token, selectedPropertyId, selectedProperty]);

  const validate = () => {
    if (!chainId || !propertyId) return 'Select a property first from Hotel Config';
    if (!String(form.item_code || '').trim()) return 'Item Code is required';
    if (!String(form.item_name || '').trim()) return 'Item Name is required';
    if (!String(form.category || '').trim()) return 'Category is required';
    if (!String(form.unit || '').trim()) return 'Unit is required';
    if (!String(form.storage_type || '').trim()) return 'Storage Type is required';
    const reorder = Number(form.reorder_level || 0);
    const par = Number(form.par_stock_level || 0);
    const cost = Number(form.cost_price || 0);
    if (Number.isNaN(reorder) || reorder < 0) return 'Reorder Level must be 0 or more';
    if (Number.isNaN(par) || par < 0) return 'Par Stock Level must be 0 or more';
    if (Number.isNaN(cost) || cost < 0) return 'Cost Price must be 0 or more';
    return '';
  };

  const save = async () => {
    setError('');
    const err = validate();
    if (err) return setError(err);
    try {
      const payload = {
        item_code: String(form.item_code || '').trim(),
        item_name: String(form.item_name || '').trim(),
        category: form.category,
        unit: form.unit,
        reorder_level: Number(form.reorder_level || 0),
        par_stock_level: Number(form.par_stock_level || 0),
        preferred_supplier: form.preferred_supplier || null,
        cost_price: Number(form.cost_price || 0),
        storage_type: form.storage_type,
        expiry_tracking: Boolean(form.expiry_tracking),
        batch_enabled: Boolean(form.batch_enabled),
        is_active: form.is_active !== false
      };
      const url = editingId ? `/api/v1/food/masters/items/${editingId}` : '/api/v1/food/masters/items';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const { message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Save failed');
      resetForm();
      loadRows();
    } catch {
      setError('Network error while saving');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      item_code: row.item_code || '',
      item_name: row.item_name || '',
      category: row.category || 'Veg',
      unit: row.unit || 'Kg',
      reorder_level: row.reorder_level ?? '',
      par_stock_level: row.par_stock_level ?? '',
      preferred_supplier: row.preferred_supplier || '',
      cost_price: row.cost_price ?? '',
      storage_type: row.storage_type || 'Dry',
      expiry_tracking: row.expiry_tracking === true,
      batch_enabled: row.batch_enabled === true,
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const removeRow = async (id) => {
    setError('');
    try {
      const res = await fetch(`/api/v1/food/masters/items/${id}`, { method: 'DELETE', headers: headers() });
      const { message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Delete failed');
      loadRows();
    } catch {
      setError('Network error while deleting');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Inventory - Item Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '1rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Item' : 'Add Item'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <input value={form.item_code} readOnly style={{ ...inputStyle, background: '#ecf0f1' }} placeholder="Item Code (Auto)" />
          <input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} style={inputStyle} placeholder="Item Name" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
            {categories.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} style={inputStyle}>
            {units.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <input type="number" min="0" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} style={inputStyle} placeholder="Reorder Level" />
          <input type="number" min="0" value={form.par_stock_level} onChange={(e) => setForm({ ...form, par_stock_level: e.target.value })} style={inputStyle} placeholder="Par Stock Level" />
          <select value={form.preferred_supplier} onChange={(e) => setForm({ ...form, preferred_supplier: e.target.value })} style={inputStyle}>
            <option value="">Preferred Supplier</option>
            {suppliers.map((s) => <option key={s.id || s.supplier_id} value={s.company_name || s.supplier_id}>{s.company_name || s.supplier_id}</option>)}
          </select>
          <input type="number" min="0" step="0.01" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} style={inputStyle} placeholder="Cost Price" />
          <select value={form.storage_type} onChange={(e) => setForm({ ...form, storage_type: e.target.value })} style={inputStyle}>
            {storageTypes.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.expiry_tracking} onChange={(e) => setForm({ ...form, expiry_tracking: e.target.checked })} />
            Expiry Tracking
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.batch_enabled} onChange={(e) => setForm({ ...form, batch_enabled: e.target.checked })} />
            Batch Enabled
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={save} className="btn-success" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{editingId ? 'Update' : 'Add'}</button>
          <button onClick={resetForm} className="btn-muted" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Reset</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Food Items</h3>
        {rows.length === 0 ? (
          <p>No food items found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Item Code</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Item Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Unit</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Cost Price</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r.item_code} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.item_code}</td>
                  <td style={{ padding: '0.6rem' }}>{r.item_name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.category}</td>
                  <td style={{ padding: '0.6rem' }}>{r.unit}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.cost_price || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editRow(r)} className="btn-primary" style={{ padding: '0.4rem 0.7rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => removeRow(r.id)} className="btn-danger" style={{ padding: '0.4rem 0.7rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodItemMaster;
